<!--

On rewrite we should consider the following:
  - make the scroller with optional header and footer fixed and only the body scrollable. - done with overlayControls at the moment but do it better
  - Initialize the stepper data with data input. - solved with specific method that transforms body data back to schema values. - During rewrite consider setter functions on specific components or something similar.

-->

<!--

============================================================================================================
||    Stepper component                                                                           ||
============================================================================================================

|   Description
------------------------------------------------------------------------------------------------------------
  Component is used to create a stepper with multiple steps. Each step is a separate component. Guiding a user through a process.

|   Features
------------------------------------------------------------------------------------------------------------
  - animation       : step transition can be animated via animate prop
  - step data       : each step can return data, which is stored in stepData object
  - step validation : each step can return a valid flag, which is used to disable/enable the next button
  - step focus      : each step can implement a focus method, which is called when the step is active. This enables the step to focus on an input field or any other element when the step becomes active.

|   Props
------------------------------------------------------------------------------------------------------------
  - steps         : Array     : List of components to render in the stepper.
  - finishBtnText : String    : Text for the last step button.
  - animate       : Boolean   : If true, animate the transition between steps.
  - type          : String    : Type of the stepper. Default is 'dots'. Can be 'images' to show images instead of dots. If images are used, the step component should have a stepImage property which is local or remote image. If the stepImage is not available then dot is shown in place of that step.

|   Events
------------------------------------------------------------------------------------------------------------
  - abort  : Emitted when the user clicks the cancel button.
  - finish : Emitted when the user clicks the finish button.

|   TODO
------------------------------------------------------------------------------------------------------------
  - dot-counter should show max 5 dots and if there are more preppend/append with '...', lighter dots, smaller dots or some other indicator
  - v-model

|   v-model
------------------------------------------------------------------------------------------------------------
  - name    : String    : required - name of the step used for final data object as key.
  - valid:  : Boolean   : optional - if ommited then the step is considered valid
  - data    : Object    : optional - contains step data
  }

|   Child step components implementation
------------------------------------------------------------------------------------------------------------
  - focus       : Component can implement focus function if there is anything to focus when it gets active.
  - setStage    : Component can implement setStage function if there is anything to do when the step is rendered. e.g. set data based on the previous step
  - editMessage : move edit message inside the Stepper component and make it a part of the stepper.

-->

<template>
  <div
    ref="stepperRef"
    class="stepper flex column flex-align-center"
    :class="{ 'overlay-controls': overlayControls }"
    :style="cssVars" >
    <!-- Your component content here -->

    <!-- Cancel overlay - prevent a user to click and edit any field until it is "safe" to edit. This is caused by the editor and should be handled correctly on rewrite. -->
    <div
      v-if="forceNoChangeAvailable || cancelButtonLoader"
      id="cancel-overlay"/>

    <div class="header">
      <!-- STEPS as images -->
      <div
        v-if="type === 'images'"
        class="image-counter flex center-all">
        <!-- <div
          v-for="(step, index) in steps"
          :key="index"
          class="dot"
          :class="[ { active: index === currentStep, 'no-image': !stepsIcons[index], 'show-hand': jumping } ]"
          :style="{ backgroundImage: `url(${stepsIcons[index]})` }"
          @click="jumpToStep(index)" /> -->
        <div
          v-for="(step, index) in steps"
          :key="index"
          class="image-counter-wrapper"
          :class="[ { active: index === currentStep, 'show-hand': jumping } ]"
          @click="jumpToStep(index)" >
          <svg
            v-if="stepsIcons[index] && typeof stepsIcons[index] === 'string' && stepsIcons[index].endsWith('.svg')"
            class="dot svg"
            :data-src="stepsIcons[index]"
            fill="currentColor" />
          <div
            v-else
            class="dot"
            :class="[ { active: index === currentStep, 'no-image': !stepsIcons[index], 'show-hand': jumping } ]"
            :style="{ backgroundImage: `url(${stepsIcons[index]})` }" />
        </div>
      </div>
      <!-- STEPS as dots - default type -->
      <div
        v-else
        class="dot-counter flex center-all">
        <div
          v-for="(step, index) in steps"
          :key="index"
          class="dot"
          :class="{ active: index === currentStep }" />
      </div>
    </div>

    <div
      ref="stepSliderRef"
      class="step-slider"
      :class="{ greedy: overlayControls }"
      :style="{ height: sliderHeight }">
      <!-- STEPS -->
      <div
        v-for="(stepComponent, index) in steps"
        :key="index"
        :ref="`step${index}Ref`"
        class="step-wrapper w-100"
        :class="{ 'no-height': index !== currentStep }"
        :style="{ left: `${(index - currentStep) * stepSliderWidth}px` }"
        @transitionend="transitionEnded" >
        <component
          :is="stepComponent.component ? stepComponent.component : stepComponent"
          :ref="`stepComponent${index}Ref`"
          :key="`${stepComponent.dat ? stepComponent.dat.stepName : 'flow'}-${resetIndex[index]}`"
          v-model="stepData[index]"
          :dat="stepComponent.dat ? stepComponent.dat : stepComponent"
          @next="goToNextStep"
          @scrollToTop="toTop" />
      </div>

      <!-- <div v-repeat="steps" v-component="user-profile" /> -->
    </div>

    <!-- FOOTER -->
    <div class="footer SB-border top flex flex-align-center justify-center" >
      <template v-if="!ignoreChangeDetection && !forceNoChangeAvailable && stepHasAChange && editMessage">
        <SBIcon
          style="margin-right: -4px;"
          :icon="SBIconList.INFO"
          :color="SBColors.bluePrimary"
          :size="12" />
        <p
          class="stepper-status-text greedy" >{{ editMessage }}</p>
      </template>
      <!-- CANCEL/BACK btn -->
      <b-button
        v-if="!editMode || (editMode && !ignoreChangeDetection && !forceNoChangeAvailable && stepHasAChange)"
        :disabled="buttonLoader"
        class="flex settings__actions--cancel"
        :class="{loading: cancelButtonLoader}"
        @click="back">
        <span>{{ editMode || currentStep === 0 ? 'Cancel' : 'Back' }}</span>
        <Spinner
          v-if="cancelButtonLoader"
          :size="14"
          :thickness="2" />
      </b-button>
      <!-- NEXT/SAVE btn -->
      <b-button
        variant="primary"
        :class="{loading: buttonLoader}"
        :disabled="cancelButtonLoader || !(!editMode || (editMode && !ignoreChangeDetection && !forceNoChangeAvailable && stepHasAChange))"
        class="flex settings__actions--save"
        @click="next">
        <span>{{ editMode || currentStep === (steps.length - 1) ? finishBtnText : 'Next' }}</span>
        <Spinner
          v-if="buttonLoader"
          :size="14"
          :thickness="2"
          :color="SBColors.white" />
      </b-button>
    </div>

    <!-- Cancel button -->
    <SBIcon
      v-if="!hideClose"
      class="close-btn"
      :icon="SBIconList.CLOSE_THIN"
      :color="SBColors.white"
      background-color="#bdc8cd"
      :hover-color="SBColors.blackPrimary"
      :border-color="SBColors.blackPrimary"
      :pointer="true"
      :size="5"
      :hover-size="15"
      :border-radius="50"
      @click="abort" />
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import { debounce } from 'debounce';

import SBColors from '@/helpers/lists/colors';
import SBIcon from '@/components/common/Icon';
import SBIconList from '@/helpers/lists/sb-icon-list';
import Spinner from '@/components/common/Spinner';
import { ModalSize } from '@/helpers/enums/modal.enum';

export default {
  name: 'Stepper',
  components: {
    SBIcon,
    Spinner,
  },
  props: {
    steps: {
      type: Array,
      required: true,
    },
    type: {
      type: String,
      required: false,
      default: 'dots',
    },
    finishBtnText: {
      type: String,
      required: false,
      default: 'Save',
    },
    animate: {
      type: Boolean,
      required: false,
      default: false,
    },
    jumping: {
      type: Boolean,
      required: false,
      default: false,
    },
    hideClose: {
      type: Boolean,
      required: false,
      default: false,
    },
    /** Next/Finish button will always be active and step will be validated when the button is pressed. */
    validateOnPress: {
      type: Boolean,
      required: false,
      default: false,
    },
    waitForChange: {
      type: Boolean,
      required: false,
      default: false,
    },
    buttonLoader: {
      type: Boolean,
      required: false,
      default: false,
    },
    overlayControls: {
      type: Boolean,
      required: false,
      default: true,
    },
    editMessage: {
      type: String,
      required: false,
      default: '',
    },
    editMode: {
      type: Boolean,
      required: false,
      default: false,
    },
    /** Used to prevent change pending modal to popup before the form is actually rendered due to change in routes or something similar. */
    ignoreChangeDetection: {
      type: Boolean,
      required: false,
      default: false,
    },
    /** Used to override the reset data function. */
    resetOverride: {
      type: Function,
      required: false,
      default: null,
    },
    /** Used to set the initial step. */
    initialStep: {
      type: Number,
      required: false,
      default: 0,
    },
    /** Used to force the cancel button loader. */
    forceCancelLoader: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  data() {
    return {
      initialized: false,
      initializedSteps: [],
      stepInicializationFinished: false,
      currentStep: 0,
      stepSliderWidth: 0,
      stepData: {},
      initialStepData: {},
      stepDataByName: {},
      sliderHeight: 0,
      allowAutofocus: true,
      forceNoChangeAvailable: false,
      stepsIcons: [],
      scrollErrorIntervalCounter: 0,
      scrollErrorInterval: null,
      scrollErrorIntervalMax: 20,
      SBColors,
      SBIconList,
      // Used to force rerender of the component which resets the data values
      resetIndex: {},
      resetTimeout: null,
      cancelButtonLoader: false,
    };
  },
  computed: {
    ...mapGetters({
      toggleElement: 'flows/toggleElement',
    }),
    cssVars() {
      return {
        '--animate-vertical-time': this.animate ? '.3s' : '0s',
        '--animate-horizontal-time': this.animate ? '.2s' : '0s',
        '--animate-opacity-time': this.animate ? '.3s' : '0s',
      };
    },
    stepInvalid() {
      return this.stepData && this.stepData[this.currentStep]
        ? !this.stepData[this.currentStep].valid
        : false;
    },
    stepHasAChange() {
      return !!Object.keys(this.toggleElement).length || !this.$lodash.isEqual(JSON.parse(JSON.stringify(this.initialStepData)), JSON.parse(JSON.stringify(this.stepData)));
    },
    disableNext() {
      return (
        !this.validateOnPress && (
          (this.waitForChange && !this.stepHasAChange) ||
          (this.waitForChange && (!this.stepInitialized || this.stepInvalid)) ||
          this.ignoreChangeDetection
        )
      );
    },
  },
  watch: {
    currentStep() {
      this.$emit('stepChanged', this.currentStep);
      try {
        this.$refs[`stepComponent${this.currentStep}Ref`][0].setStage();
      } catch (e) {
        this.recalcSliderHeight();
      }
      setTimeout(() => {
        this.recalcSliderHeight();
        this.allowAutofocus = true;
        if (!this.animate) this.transitionEnded();
        // console.log('FOCUS ON STEP', this.currentStep);
        // this.$refs[`stepComponent${this.currentStep}Ref`][0].focus();
        // this.$refs[`stepComponent${this.currentStep}Ref`][0].blurInputs();
      }, 0);
    },
    stepData: {
      handler() {
        setTimeout(() => {
          this.recalcSliderHeight();
          this.setStepDataByName();
        }, 0);
      },
      deep: true,
    },
    steps() {
      setTimeout(() => {
        this.initialStepData = JSON.parse(JSON.stringify(this.stepData));
      }, 3000);
    },
    stepHasAChange() {
      if (!this.stepHasAChange) {
        // setTimeout(() => {
        this.$eventBus.$emit('clearEditMessage');
        // }, 0);
      }
    },
  },
  mounted() {
    this.stepSliderWidth = this.$refs.stepSliderRef ? this.$refs.stepSliderRef.offsetWidth : 0;
    if (!this.animate) this.transitionEnded();
    if (this.initialStep) this.currentStep = this.initialStep;
    if (this.forceCancelLoader) this.cancelButtonLoader = true;
    this.initialized = true;
    this.recalcSliderHeight();
    // Set step images
    this.stepsIcons = this.steps.map((step) => {
      if (step.stepImage && step.stepImage) {
        return step.stepImage;
      }
      return null;
    });
    this.steps.forEach((step, index) => {
      this.resetIndex[index] = 0;
    });
    this.resetIndex = { ...this.resetIndex };
    window.eventBus.$on('stepInit', this.stepInit);
    window.eventBus.$on('height-changed', this.recalcSliderHeight);
    this.$eventBus.$on('resetInitialData', () => {
      // this.initialStepData = JSON.parse(JSON.stringify(this.stepData));
      this.forceNoChangeAvailable = false;
    });

    if (window.addEventListener) {
      window.addEventListener('beforeunload', this.closeHandler);
      window.addEventListener('close', this.closeHandler);
    } else if (window.attachEvent) {
      window.attachEvent('onbeforeunload', this.closeHandler);
      window.addEventListener('close', this.closeHandler);
    }

    this.$eventBus.$on('setBlockingOverlay', (state) => {
      this.forceNoChangeAvailable = state;
    });

    this.$eventBus.$on('clearAddFiles', () => this.resetData(true));
    this.$eventBus.$on('sync-data', this.syncData);
  },
  destroyed() {
    window.eventBus.$off('stepInit', this.stepInit);
    window.eventBus.$off('height-changed', this.recalcSliderHeight);
    this.$eventBus.$off('resetInitialData');
    this.$eventBus.$off('clearAddFiles', () => this.resetData(true));
    this.$eventBus.$off('sync-data', this.syncData);
    if (window.removeEventListener) {
      window.removeEventListener('beforeunload', this.closeHandler);
      window.removeEventListener('close', this.closeHandler);
    } else if (window.detachEvent) {
      window.detachEvent('onbeforeunload', this.closeHandler);
      window.removeEventListener('close', this.closeHandler);
    }
  },
  methods: {
    ...mapActions({
      setStepperDataStore: 'helpers/setStepperDataStore',
      clearToggleElements: 'flows/clearToggleElements',
    }),
    // Prevent closing the page or relaoding it if there are unsaved changes
    closeHandler(e) {
      if (!this.stepHasAChange) {
        return;
      }

      const defaultMessage = 'Changes that you made may not be saved.';
      if (this.stepHasAChange) {
        e.preventDefault(); // required in some browsers
        e.returnValue = defaultMessage; // required in some browsers
      }
      return defaultMessage; // only works in old browsers
    },
    stepInit(data) {
      this.initializedSteps.push(data.step);
      if (this.initializedSteps.length === this.steps.length) {
        // Focus on the first step input
        this.$refs[`stepComponent${this.currentStep}Ref`][0].focus();
        setTimeout(() => {
          this.stepInicializationFinished = true;
          this.initialStepData = JSON.parse(JSON.stringify(this.stepData));
          window.eventBus.$emit('stepperInitialized');
          this.cancelButtonLoader = false;
        }, 1000);
      }
    },
    resetData(force = false) {
      if (!force && this.resetOverride) {
        this.cancelButtonLoader = true;
        this.resetOverride();
        return;
      }
      this.forceNoChangeAvailable = true;
      this.resetIndex[this.currentStep]++;
      clearTimeout(this.resetTimeout);
      this.resetTimeout = setTimeout(() => {
        this.initialStepData = JSON.parse(JSON.stringify(this.stepData));
        this.clearToggleElements();
        if (this.$refs[`stepComponent${this.currentStep}Ref`][this.currentStep]) {
          this.$refs[`stepComponent${this.currentStep}Ref`][this.currentStep].blurInputs();
        }
      }, 1000);
    },
    back() {
      if (this.editMode) {
        this.resetData();
      } else if (this.currentStep === 0) {
        this.abort();
      } else {
        this.currentStep--;
        this.$nextTick(() => {
          this.toTop();
        });
      }
    },
    goToNextStep() {
      if (!(this.buttonLoader || this.stepInvalid)) this.next();
    },
    next() {
      // validate and scroll to the error field if it exists
      // if (this.validateOnPress && this.stepInvalid) {
      if (this.stepInvalid) {
        this.$refs[`stepComponent${this.currentStep}Ref`][0].forceValidation();
        this.scrollErrorInterval = setInterval(() => {
          try {
            this.$refs.stepperRef.querySelector('.error:not(.non-blocking):not(.warning)').parentElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
            clearInterval(this.scrollErrorInterval);
          } catch (error) {
            this.scrollErrorIntervalCounter++;
          }
          if (this.scrollErrorIntervalCounter >= this.scrollErrorIntervalMax) {
            clearInterval(this.scrollErrorInterval);
          }
        }, 10);
        return;
      }
      if (this.editMode) {
        this.finish();
        return;
      }
      if (this.currentStep === (this.steps.length - 1)) {
        this.finish();
      } else {
        this.currentStep++;
      }
      this.$nextTick(() => {
        this.toTop();
      });
    },
    jumpToStep(index, force = false) {
      if (this.jumping || force) {
        // Ask the step component if it is allowed to jump. Component could prevent the jump if the changes are not saved.
        try {
          if (force) {
            this.currentStep = index;
          } else if (this.$refs[`stepComponent${this.currentStep}Ref`][0].allowJump()) {
            if (this.stepHasAChange) {
              this.$numberInfoModal.show({
                size: ModalSize.MEDIUM,
                title: 'Unsaved changes',
                message: 'You have unsaved changes. Are you sure you want to leave? All changes will be lost.',
                confirmButtonText: 'Yes, continue',
                confirmCallback: () => {
                  this.resetData(true);
                  this.currentStep = index;
                },
              });
            } else {
              this.currentStep = index;
            }
          }
          this.toTop();
        } catch (e) {
          // allowJump method not implemented
        }
      }
    },
    /**
     * Function to be called when the user wants to navigate away from the route. It can be called from outside from the route component.
     * @param callback Callback function to be called if it is possible or allowed to navigate away from the route. At most times it would be "next" method from the router.
     */
    navigateAway(callback = () => {}) {
      if (this.stepHasAChange) {
        this.$numberInfoModal.show({
          size: ModalSize.MEDIUM,
          title: 'Unsaved changes',
          message: 'You have unsaved changes. Are you sure you want to leave? All changes will be lost.',
          confirmButtonText: 'Yes, continue',
          confirmCallback: () => {
            callback();
          },
        });
      } else {
        callback();
      }
    },
    /**
     * Function to be called when the user wants to sync the data after the async data changed. e.g. when urls are updated via pusher.
     * @param data Object with props to be synced.
     */
    syncData(data) {
      this.$nextTick(() => {
        const newData = this.getObjectCopyByKey(this.stepData, data.propName);
        const path = this.getPathForTheProvidedKey(this.initialStepData, data.propName);
        this.setPathValue(this.initialStepData, path, newData);
      });
    },
    /**
     * Function to be called when the user wants to set the value of a path in an object.
     * @param object Object to be updated.
     * @param path Path to the value to be updated.
     * @param value Value to be set.
     */
    setPathValue(object, path, value) {
      path = path ? path.split('.') : [];
      const setValue = (o, p, v) => {
        const firstPath = p.shift();
        try {
          if (p.length > 0) {
            setValue(o[firstPath], p, v);
          } else {
            o[firstPath] = v;
          }
        } catch (e) { console.log(e); }
      };
      setValue(object, path, value);
    },
    toTop() {
      setTimeout(() => {
        // On rewrite meke this an internal stepper feature so there is no need to emit the value to a wrapper scroll component.
        this.$emit('scrollToTop');
      }, 0);
    },
    abort() {
      this.$emit('abort');
    },
    finish() {
      this.$emit('finish', { ...this.stepDataByName });
    },
    recalcSliderHeight() {
      this.sliderHeight = this.$refs[`step${this.currentStep}Ref`] ? `${this.$refs[`step${this.currentStep}Ref`][0].offsetHeight + 54}px` : 0; // 54 is the height of the stepper header buttons
    },
    setStepDataByName() {
      const stepDataByName = {};
      try {
        Object.keys(this.stepData).forEach((stepNumber) => {
          const stepName = this.stepData[stepNumber].name;
          stepDataByName[stepName] = this.stepData[stepNumber].data;
        });
        // const equal = this.$lodash.isEqual(JSON.parse(JSON.stringify(this.stepDataByName)), JSON.parse(JSON.stringify(stepDataByName)));
        // if (this.stepInicializationFinished && !equal) {
        //   this.$eventBus.$emit('editMessage', {
        //     key: 'default',
        //     message: 'apply changes',
        //   });
        // }
        this.stepDataByName = { ...stepDataByName };
      } catch (error) {
        this.stepDataByName = {};
      }
      this.setStepperDataStore(this.stepDataByName);
    },

    transitionEnded() {},
    // transitionEnded: debounce(function f() {
      // Autofocus is allowed only on initial step load
      // console.log('-----------------');
      // if (this.allowAutofocus) {
      //   console.log('----------------- allowed');
      //   try {
      //     console.log('----------------- GOOOOOO');
      //     console.log('----------------- FOCUS ON STEP', this.currentStep);
      //     this.$refs[`stepComponent${this.currentStep}Ref`][0].focus();
      //     this.allowAutofocus = false;
      //   } catch (e) { /* Do nothing */ }
      // }
    // }, 200),
  },
};
</script>

<style lang="scss" scoped>
.stepper {
  position: relative;

  #cancel-overlay {
    position: absolute;
    top: 0;
    left: 0px;
    right: 0px;
    bottom: 0;
    background-color: white;
    z-index: 9991;
    opacity: 0;
  }

  &.overlay-controls {
    height: 100%;
    overflow: auto;

    .header {
      position: sticky;
      top: 0;
      background: $white;
      width: 100%;
      z-index: 999;
      padding-bottom: 20px;
      margin-bottom: -20px;

      .image-counter {
        position: sticky;
        top: 20px;
        background: $white;
        z-index: 999;
        width: fit-content;
      }
    }

    .footer {
      position: sticky;
      bottom: 0;
      background: $white;
      z-index: 999;
    }
  }

  // background-color: lightcyan;
  .dot-counter {
    .dot {
      width: 6px;
      height: 6px;
      border-radius: 50%;
      background-color: $greyLight;
      margin: 0 4px 0 0;
      &.active {
        background-color: $blackPrimary;
      }
    }
  }

  .image-counter {
    border-radius: 10px;
    box-shadow: 0 1px 3px 0 rgba(48, 54, 70, 0.1);
    border: solid 1px $greyBorder;
    background-color: $white;
    overflow: hidden;
    margin: 0 auto;

    .image-counter-wrapper {
      .dot {
        width: 41px;
        height: 32px;
        background-size: 16px 16px;
        background-position: center;
        background-repeat: no-repeat;
        &.active {
          background-color: #f0f7ff;
        }

        &.svg {
          width: 16px;
          height: 16px;
          margin: 8px 12px;
          fill: $blackUltralight;
        }

        &.no-image {
          display: flex;
          align-items: center;
          justify-content: center;
          &:after {
            content: '';
            display: block;
            width: 6px;
            height: 6px;
            background-color: $blackPrimary;
            border-radius: 50%;
          }
        }
      }

      &.active {
        background-color: #f0f7ff;
        .dot.svg {
          fill: $blackPrimary;
        }
      }
    }

    & > div:not(:last-child) {
      border-right: solid 1px $greyBorder;
    }
  }

  .step-slider {
    position: relative;
    width: 100%;
    // overflow: hidden; // Uncomment if you want to have an animation
    transition: height var(--animate-vertical-time) ease-in;

    .step-wrapper {
      // position: absolute;
      top: 0;
      padding: 20px 50px 60px;
      transition: left var(--animate-horizontal-time) ease-in, opacity var(--animate-opacity-time) cubic-bezier(1,0,.86,.69);

      // Not used any more because it is possible to vlick on the element even if it is not visible
      &.opacity-0 {
        transition: left var(--animate-horizontal-time) ease-in, opacity var(--animate-opacity-time) cubic-bezier(0,1,.69,.86);
      }

      &.no-height {
        height: 0;
        overflow: hidden;
        padding: 0 !important;
      }
    }
  }

  .footer {
    width: 100%;
    padding: 33px;
    gap: 10px;

    .stepper-status-text {
      color: $bluePrimary;
      font-weight: 500;
      font-size: 14px;
    }
  }

  .close-btn {
    position: absolute;
    top: 9px;
    right: 50px;
    z-index: 9999;
  }
}
</style>
